import { Application, Container } from 'pixi.js';
import { DRAG_CIRCLE_DIAMETER, WALL_THICKNESS, PLAYABLE_AREA } from './constants';
import defaultSettings from './defaultSettings';
import { calculateForce, checkIfMobile } from './utils';
import BallManager from './ballManager';
import GameStateManager from './gameStateManager';
import RenderManager from './renderManager';
import ViewportManager from './viewportManager';
import UIManager from './uiManager';
import PlayerList from './playerList';
import Chat from './chat';

export default class InputManager {
    private app: Application;
    private ballManager: BallManager;
    private gameStateManager: GameStateManager;
    private renderManager: RenderManager;
    private gameWorld: Container;
    private viewportManager: ViewportManager;
    private uiManager: UIManager;
    private playerList: PlayerList;
    private chat: Chat;
    private isMouseInput: boolean;
    private settings: any;
    private currentControlType: string;
    private mousePosition: { screen: { x: number, y: number }, world: { x: number, y: number } };
    private forceText: any;
    private cursorBounce: number;
    private lastMouseEvent: MouseEvent | null;
    private touchStartPosition: { x: number, y: number } | null;
    private dragStartPosition: { x: number, y: number } | null;
    private currentDragPosition: { x: number, y: number } | null;
    private isClickDragMode: boolean;
    private puttingLine: any;

    private horizontalMargin: number = 0.1;
    private verticalMargin: number = 0.1;
    private dragZoneHeightPercentage: number = 0.4;
    private dragZoneWidth: number = 0;
    private dragZoneHeight: number = 0;
    private dragZoneLeft: number = 0;
    private dragZoneTop: number = 0;
    private scrollPosition: number = 0;

    private leftJoystickY: number = 0;
    private rightJoystickX: number = 0;
    private rightJoystickY: number = 0;
    private buttonAPressed: boolean = false;
    private buttonBPressed: boolean = false;
    private buttonYPressed: boolean = false;
    private buttonXPressed: boolean = false;

    // Touch detection for two-finger scrolling
    private touchCount: number = 0;
    private isScrolling: boolean = false;

    constructor(app: Application, ballManager: BallManager, gameStateManager: GameStateManager, renderManager: RenderManager, gameWorld: Container, viewportManager: ViewportManager, uiManager: UIManager, playerList: PlayerList, chat: Chat) {
        this.app = app;
        this.ballManager = ballManager;
        this.gameStateManager = gameStateManager;
        this.renderManager = renderManager;
        this.gameWorld = gameWorld;
        this.viewportManager = viewportManager;
        this.uiManager = uiManager;
        this.playerList = playerList;
        this.chat = chat;
        this.isMouseInput = true;

        this.settings = JSON.parse(localStorage.getItem('golfUpSettings')!) || defaultSettings;
        this.currentControlType = this.settings.player.controlType;

        this.mousePosition = { screen: { x: 0, y: 0 }, world: { x: 0, y: 0 } };
        this.forceText = this.renderManager.createForceText();

        this.cursorBounce = 0;

        this.lastMouseEvent = null;

        this.touchStartPosition = null;

        this.leftJoystickY = 0;
        this.rightJoystickX = 0;
        this.rightJoystickY = 0;
        this.buttonAPressed = false;
        this.buttonBPressed = false;
        this.buttonYPressed = false;
        this.buttonXPressed = false;

        this.horizontalMargin = 0.1; // 10% of viewport width
        this.verticalMargin = 0.1; // 20% of viewport height
        this.isClickDragMode = false;
        this.dragStartPosition = null;
        this.currentDragPosition = null;
        this.puttingLine = null;
        this.updateDragZoneDimensions();

        this.setupEventListeners();
        this.setInitialScrollState();
        this.setupControlScheme(this.currentControlType);
        window.addEventListener('resize', this.handleResize.bind(this));


    }

    setupControlScheme(controlType: string) {
        this.currentControlType = controlType;
        if (controlType === "click-drag" || checkIfMobile()) {
            this.setupClickDragMode();
        } else {
            this.setupOneClickMode();
        }
    }

    setupOneClickMode() {
        this.isClickDragMode = false;
        this.dragStartPosition = null;
        this.currentDragPosition = null;
        this.puttingLine = null;
        this.renderManager.clearDragZone();
        this.renderManager.clearCursorGraphics();
    }


    setupEventListeners() {
        this.app.canvas.addEventListener('mousemove', (e) => {
            this.isMouseInput = true;
            this.handleMouseMove(e);
        });

        this.app.canvas.addEventListener('touchstart', (e) => {
            this.isMouseInput = false;
            this.handleTouchStart(e);
        });
        this.app.canvas.addEventListener('mousedown', this.handleMouseDown.bind(this));
        this.app.canvas.addEventListener('mouseup', this.handleClickEnd.bind(this));
        this.app.canvas.addEventListener('touchstart', this.handleTouchStart.bind(this));
        this.app.canvas.addEventListener('touchmove', this.handleTouchMove.bind(this));
        this.app.canvas.addEventListener('touchend', this.handleClickEnd.bind(this));
        window.addEventListener('wheel', this.handleScroll.bind(this), { passive: false });

        // prevent right click context menu
        this.app.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    }

    handleScroll(event: WheelEvent) {
        // Don't scroll game world if popup is open or campaign UI is visible
        if (this.uiManager?.popupManager?.open) return;
        const campaignUI = document.getElementById('campaign-ui');
        if (campaignUI && campaignUI.parentElement) return; // Campaign UI is visible
        const scrollChanged = this.viewportManager.updateScrollPosition(event.deltaY);
        if (scrollChanged) {
            this.updateMousePositionOnScroll();
        }
    }

    setInitialScrollState() {
        this.scrollPosition = 0;
        this.gameWorld.y = 0;
        this.viewportManager.updateScrollPosition(this.scrollPosition);
    }

    handleMouseDown(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (event.button === 0) {
            // Track mouse button state for game modes (e.g., SnakeUp permanent segments)
            this.ballManager.setMouseButtonDown(true);
        }
        if (this.gameStateManager.roomState.roundStartTime + (this.gameStateManager.roomState.roundDuration || 0) >= Date.now()) {
            if (event.button === 0) {
                if (this.isClickDragMode) {
                    if (this.isInsideDragZone(this.mousePosition.screen)) {
                        this.dragStartPosition = this.mousePosition.screen;
                        this.currentDragPosition = this.mousePosition.screen;
                        this.updateDragLine();
                    }
                } else {
                    this.ballManager.strike(this.mousePosition.world.x, this.mousePosition.world.y);
                }
            } else if (event.button === 2) {
                // Right-click: handle game mode specific actions (e.g., delete persistent platforms in SnakeUp)
                this.ballManager.handleRightClick(this.mousePosition.world.x, this.mousePosition.world.y);
            }
        }
    }

    handleClickEnd(event: MouseEvent | TouchEvent) {
        // Track mouse/touch button state for game modes (e.g., SnakeUp permanent segments)
        if (event instanceof MouseEvent && event.button === 0) {
            this.ballManager.setMouseButtonDown(false);
        } else if (event instanceof TouchEvent) {
            this.ballManager.setMouseButtonDown(false);
            // Reset touch state
            this.touchCount = 0;
            this.isScrolling = false;
        }

        if (this.gameStateManager.isGameOver) return;


        // Don't execute swing if we were scrolling
        if (event instanceof TouchEvent && this.isScrolling) {
            this.resetDragState();
            return;
        }

        if (this.isClickDragMode && this.dragStartPosition && this.currentDragPosition) {
            const dx = this.currentDragPosition.x - this.dragStartPosition.x;
            const dy = this.currentDragPosition.y - this.dragStartPosition.y;
            const dragDistance = Math.sqrt(dx * dx + dy * dy);

            const minDragDistance = 5;

            if (dragDistance >= minDragDistance) {
                const ballPosition = this.ballManager.getPosition();
                const virtualPutPosition = {
                    x: ballPosition.x + dx + WALL_THICKNESS,
                    y: ballPosition.y + dy
                };

                this.ballManager.strike(virtualPutPosition.x, virtualPutPosition.y);
            }
            this.resetDragState();
        }

        if (this.isMouseInput) {
            this.updateAimingGraphics(this.mousePosition);
        }
    }

    isInsideCenterCircle(position: { x: number, y: number }) {
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight * 0.75;
        const dx = position.x - centerX;
        const dy = position.y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return distance <= DRAG_CIRCLE_DIAMETER / 2;
    }

    updateDragLine() {
        if (this.dragStartPosition && this.currentDragPosition) {
            this.renderManager.updatePuttingLine(this.dragStartPosition, this.currentDragPosition);
            this.updateDragCursorGraphics(this.currentDragPosition.x, this.currentDragPosition.y);
        } else {
            this.renderManager.clearCursorGraphics();
            this.renderManager.clearPuttingLine();
        }
    }

    updateDragCursorGraphics(endX: number, endY: number) {
        if (!this.dragStartPosition) return;

        const dx = endX - this.dragStartPosition.x;
        const dy = endY - this.dragStartPosition.y;
        const dragDistance = Math.sqrt(dx * dx + dy * dy);

        const maxDragDistance = Math.max(this.dragZoneWidth, this.dragZoneHeight);
        const normalizedDragDistance = Math.min(dragDistance / maxDragDistance, 1);
        const force = normalizedDragDistance * (0.04 - 0.002) + 0.002;

        const normalisedForce = Math.floor(normalizedDragDistance * 9) + 1;
        const _pos = this.touchStartPosition ? this.touchStartPosition : { x: endX, y: endY }
        const position = this.getMousePosition(this.lastMouseEvent ? this.lastMouseEvent : { clientX: endX, clientY: endY } as MouseEvent);
        this.renderManager.updateCursorTriangle(
            position.screen,
            _pos,
            force,
            this.cursorBounce,
            this.isClickDragMode
        );
        this.renderManager.updateForceText(this.forceText, normalisedForce, endX, endY);
    }

    handleTouchStart(event: TouchEvent) {
        event.preventDefault();
        this.isMouseInput = false;

        // Count touches to detect two-finger scrolling
        this.touchCount = event.touches.length;

        // Two or more fingers = scrolling mode
        if (this.touchCount >= 2) {
            this.isScrolling = true;
            // Track both touches for scrolling
            const touch1 = event.touches[0];
            const touch2 = event.touches[1];
            this.viewportManager.lastTouchY = (touch1.clientY + touch2.clientY) / 2;
            this.viewportManager.isTouching = true;
            return; // Exit early, don't start drag
        }

        // One finger = drag/swing mode
        this.isScrolling = false;
        this.ballManager.setMouseButtonDown(true);
        const touch = event.touches[0];
        this.touchStartPosition = {
            x: touch.clientX,
            y: touch.clientY
        };

        // Full screen drag zone - no center circle check needed
        if (this.isClickDragMode) {
            this.dragStartPosition = this.touchStartPosition;
            this.currentDragPosition = this.touchStartPosition;
            this.updateDragLine();
        }

        this.viewportManager.lastTouchY = touch.clientY;
        this.viewportManager.isTouching = true;
    }

    handleTouchMove(event: TouchEvent) {
        event.preventDefault();

        // Update touch count
        this.touchCount = event.touches.length;

        // Two or more fingers = scrolling
        if (this.touchCount >= 2 || this.isScrolling) {
            this.isScrolling = true;
            // Use average of both touches for scrolling
            const touch1 = event.touches[0];
            const touch2 = event.touches[1];
            const avgY = touch2 ? (touch1.clientY + touch2.clientY) / 2 : touch1.clientY;
            const deltaY = this.viewportManager.lastTouchY - avgY;
            this.viewportManager.updateScrollPosition(deltaY);
            this.viewportManager.lastTouchY = avgY;
            // Clear any drag state when scrolling
            if (this.dragStartPosition) {
                this.resetDragState();
            }
            return;
        }

        // One finger = drag/swing
        this.isScrolling = false;
        const touch = event.touches[0];
        const currentPosition = {
            x: touch.clientX,
            y: touch.clientY
        };

        if (this.isClickDragMode && this.dragStartPosition) {
            this.currentDragPosition = currentPosition;
            this.updateDragLine();
        } else {
            // Fallback: if not in drag mode, allow scrolling with one finger
            const deltaY = this.viewportManager.lastTouchY - currentPosition.y;
            this.viewportManager.updateScrollPosition(deltaY);
            this.viewportManager.lastTouchY = currentPosition.y;
        }
    }

    handleResize() {
        this.updateDragZoneDimensions();
        if (this.isClickDragMode) {
            this.renderManager.updateDragZone(this.dragZoneWidth, this.dragZoneHeight, this.dragZoneLeft, this.dragZoneTop);
        }
    }


    updateDragZoneDimensions() {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Full screen drag zone for mobile - no margins
        this.horizontalMargin = 0;
        this.verticalMargin = 0;
        this.dragZoneHeightPercentage = 1.0; // Full height

        this.dragZoneWidth = viewportWidth;
        this.dragZoneHeight = viewportHeight;
        this.dragZoneLeft = 0;
        this.dragZoneTop = 0;
    }

    setupClickDragMode() {
        this.isClickDragMode = true;
        this.dragStartPosition = null;
        this.currentDragPosition = null;
        this.puttingLine = this.renderManager.createPuttingLine();
        this.updateDragZoneDimensions();
        // Full screen drag zone - still create it for visual feedback if needed, but it covers entire screen
        this.renderManager.createDragZone(this.dragZoneWidth, this.dragZoneHeight, this.dragZoneLeft, this.dragZoneTop);
        this.renderManager.clearCursorGraphics();
    }

    isInsideDragZone(position: { x: number, y: number }) {
        // Full screen drag zone - always return true
        return true;
    }

    getMousePosition(event: MouseEvent) {
        const rect = this.app.canvas.getBoundingClientRect();
        const screenX = event.clientX - rect.left;
        const screenY = event.clientY - rect.top;

        let worldX, worldY;

        if (window.innerWidth <= PLAYABLE_AREA) {
            const scale = window.innerWidth / PLAYABLE_AREA;
            worldX = (screenX / scale) + WALL_THICKNESS * 2;
            worldY = (screenY / scale) + this.viewportManager.scrollPosition;
        } else {
            worldX = this.viewportManager.getWorldXFromScreenX(screenX) + WALL_THICKNESS;
            worldY = this.viewportManager.getWorldYFromScreenY(screenY);
        }

        return {
            screen: { x: screenX, y: screenY },
            world: { x: worldX, y: worldY }
        };
    }

    updateMousePositionOnScroll() {
        if (this.lastMouseEvent) {
            const pos = this.getMousePosition(this.lastMouseEvent);
            this.mousePosition.screen = pos.screen;
            this.updateAimingGraphics(this.mousePosition);
        }
    }

    updateMousePositionRelativeToViewport() {
        if (this.lastMouseEvent) {
            const pos = this.getMousePosition(this.lastMouseEvent);
            this.mousePosition.screen = pos.screen;
            this.updateAimingGraphics(this.mousePosition);
        }
    }

    updateAimingGraphics(position: { screen: { x: number, y: number }, world: { x: number, y: number } }) {
        if (!position || !position.world || !position.screen) return;
        const ballPosition = this.ballManager.getPosition();
        if (!ballPosition) return;

        const forceOffset = parseFloat(localStorage.getItem('forceOffset')!) || defaultSettings.player.forceOffset;
        const force = calculateForce(position.world.x - WALL_THICKNESS, position.world.y, ballPosition.x, ballPosition.y, false, forceOffset);
        const rangeDiff = 0.04 - 0.002;
        const normalisedForce = Math.floor((force - 0.002) / rangeDiff * 9) + 1;

        const screenBallPosition = {
            x: this.viewportManager.getScreenXFromWorldX(ballPosition.x),
            y: this.viewportManager.getScreenYFromWorldY(ballPosition.y)
        };

        const shouldShowCursor = this.isMouseInput ||
            (this.isClickDragMode && this.dragStartPosition) ||
            (!this.isClickDragMode && !this.isMouseInput);

        if (shouldShowCursor) {
            this.renderManager.updateCursorTriangle(
                position.screen,
                this.isClickDragMode ? { x: window.innerWidth / 2, y: window.innerHeight * 0.75 } : screenBallPosition,
                force,
                this.cursorBounce,
                this.isClickDragMode
            );
            this.renderManager.updateForceText(this.forceText, normalisedForce, position.screen.x, position.screen.y);
        } else {
            this.renderManager.clearCursorGraphics();
        }

        if (!this.isClickDragMode) {
            this.renderManager.updateThroughLine(position.world, ballPosition, normalisedForce);
        }
    }
    handleMouseMove(event: MouseEvent) {
        this.isMouseInput = true;
        this.lastMouseEvent = event;
        this.mousePosition = this.getMousePosition(event);
        this.updateAimingGraphics(this.mousePosition);
        if (this.isClickDragMode && this.dragStartPosition) {
            this.currentDragPosition = this.mousePosition.screen;
            this.updateDragLine();
        }
    }
    clearAimingGraphics() {
        this.forceText.visible = false;
        this.renderManager.clearAimingGraphics();
    }


    updateMousePositionWithJoystick(deltaX: number, deltaY: number) {
        const newScreenX = this.mousePosition.screen.x + deltaX;
        const newScreenY = this.mousePosition.screen.y + deltaY;
        const newPosition = this.getMousePosition({ clientX: newScreenX, clientY: newScreenY } as MouseEvent);
        this.mousePosition.screen = newPosition.screen;
        this.updateAimingGraphics(this.mousePosition);
    }

    peekPlayerList(show: boolean) {
        this.playerList.toggleVisibility(show);
    }

    toggleChatInputFocus() {
        this.chat.toggleChatInputFocus();
    }

    reset() {
        this.clearAimingGraphics();
    }

    update(delta: number) {
        // we should also call this.renderManager.updateThroughLine, so its always pointing to the ball
        if (!this.mousePosition.world || !this.ballManager.getPosition()) return
        if (this.settings.player.controlType !== "click-drag") {
            this.renderManager.updateThroughLine(this.mousePosition.world, this.ballManager.getPosition(), 10);
        }
    }

    resetDragState() {
        this.dragStartPosition = null;
        this.currentDragPosition = null;
        this.renderManager.clearPuttingLine();
        if (!this.isMouseInput) {
            this.renderManager.clearCursorGraphics();
        }
    }
}